import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../api";
import "./AdminAvatarGallery.css";

type AvatarItem = {
  id: string;
  label?: string;
  src?: string;
  url?: string;
  file_name?: string;
  filename?: string;
  name?: string;
  image_url?: string;
  public_url?: string;
  path?: string;
};

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "https://doctorisin-genesis-1-0.onrender.com").replace(/\/+$/, "");

function clean(v: any): string {
  return String(v ?? "").trim();
}

function safeId(v: any, index: number): string {
  const raw =
    clean(v?.id) ||
    clean(v?.file_name) ||
    clean(v?.filename) ||
    clean(v?.name) ||
    `avatar_${index + 1}`;
  return raw;
}

function normalizeOneAvatar(raw: any, index: number): AvatarItem {
  if (typeof raw === "string") {
    const id = clean(raw);
    return {
      id: id || `avatar_${index + 1}`,
      label: id || `Avatar ${index + 1}`,
      src: id,
    };
  }

  const id = safeId(raw, index);
  const label =
    clean(raw?.label) ||
    clean(raw?.title) ||
    clean(raw?.display_name) ||
    clean(raw?.name) ||
    id;

  return {
    id,
    label,
    src: clean(raw?.src),
    url: clean(raw?.url),
    file_name: clean(raw?.file_name),
    filename: clean(raw?.filename),
    name: clean(raw?.name),
    image_url: clean(raw?.image_url),
    public_url: clean(raw?.public_url),
    path: clean(raw?.path),
  };
}

function unwrapAvatars(data: any): AvatarItem[] {
  const arr =
    (Array.isArray(data) && data) ||
    (Array.isArray(data?.avatars) && data.avatars) ||
    (Array.isArray(data?.items) && data.items) ||
    (Array.isArray(data?.results) && data.results) ||
    (Array.isArray(data?.files) && data.files) ||
    [];

  return (arr as any[]).map(normalizeOneAvatar).filter((x) => !!clean(x.id));
}

function pickSrc(a: AvatarItem): string {
  return (
    clean(a.src) ||
    clean(a.url) ||
    clean(a.image_url) ||
    clean(a.public_url) ||
    clean(a.path) ||
    clean(a.file_name) ||
    clean(a.filename) ||
    clean(a.name) ||
    clean(a.id)
  );
}

function normalizeAvatarUrl(raw: string, fallbackId?: string) {
  const v = clean(raw) || clean(fallbackId);
  if (!v) return "";

  if (v.startsWith("http://") || v.startsWith("https://")) return v;
  if (v.startsWith("data:")) return v;

  if (!v.startsWith("/")) {
    return `${API_BASE_URL}/public/avatars/${encodeURIComponent(v)}`;
  }

  return `${API_BASE_URL}${v}`;
}

function cacheBust(url: string) {
  if (!url) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}t=${Date.now()}`;
}

async function getWithSlashFallback(url: string, config?: any) {
  try {
    return await api.get(url, config);
  } catch (err: any) {
    const status = err?.response?.status;
    const shouldRetry = !err?.response || status === 307 || status === 308 || status === 404;
    if (shouldRetry && !url.endsWith("/")) return await api.get(url + "/", config);
    throw err;
  }
}

async function postWithSlashFallback(url: string, data?: any, config?: any) {
  try {
    return await api.post(url, data, config);
  } catch (err: any) {
    const status = err?.response?.status;
    const shouldRetry = !err?.response || status === 307 || status === 308 || status === 404;
    if (shouldRetry && !url.endsWith("/")) return await api.post(url + "/", data, config);
    throw err;
  }
}

async function putWithSlashFallback(url: string, data?: any, config?: any) {
  try {
    return await api.put(url, data, config);
  } catch (err: any) {
    const status = err?.response?.status;
    const shouldRetry = !err?.response || status === 307 || status === 308 || status === 404;
    if (shouldRetry && !url.endsWith("/")) return await api.put(url + "/", data, config);
    throw err;
  }
}

async function delWithSlashFallback(url: string, config?: any) {
  try {
    return await api.delete(url, config);
  } catch (err: any) {
    const status = err?.response?.status;
    const shouldRetry = !err?.response || status === 307 || status === 308 || status === 404;
    if (shouldRetry && !url.endsWith("/")) return await api.delete(url + "/", config);
    throw err;
  }
}

async function getTry(urls: string[], config?: any) {
  let lastErr: any = null;
  for (const u of urls) {
    try {
      return await getWithSlashFallback(u, config);
    } catch (e: any) {
      lastErr = e;
    }
  }
  throw lastErr;
}

async function postTry(urls: string[], data?: any, config?: any) {
  let lastErr: any = null;
  for (const u of urls) {
    try {
      return await postWithSlashFallback(u, data, config);
    } catch (e: any) {
      lastErr = e;
    }
  }
  throw lastErr;
}

async function delTry(urls: string[], config?: any) {
  let lastErr: any = null;
  for (const u of urls) {
    try {
      return await delWithSlashFallback(u, config);
    } catch (e: any) {
      lastErr = e;
    }
  }
  throw lastErr;
}

async function tryJsonThenForm(urls: string[], data: Record<string, any>) {
  let lastErr: any = null;

  for (const url of urls) {
    try {
      return await putWithSlashFallback(url, data);
    } catch (e: any) {
      lastErr = e;
      const status = e?.response?.status;

      if (status === 400 || status === 404 || status === 405 || status === 415 || status === 422) {
        try {
          const fd = new FormData();
          Object.entries(data).forEach(([k, v]) => fd.append(k, String(v ?? "")));
          return await putWithSlashFallback(url, fd);
        } catch (e2: any) {
          lastErr = e2;
        }
      }
    }
  }

  throw lastErr;
}

export default function AdminAvatarGallery() {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [avatars, setAvatars] = useState<AvatarItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const [editingId, setEditingId] = useState<string>("");
  const [draftLabel, setDraftLabel] = useState<string>("");

  const avatarMap = useMemo(() => {
    const m = new Map<string, AvatarItem>();
    avatars.forEach((a) => m.set(a.id, a));
    return m;
  }, [avatars]);

  const listUrls = useMemo(
    () => [
      `/admin/avatars?_=${Date.now()}`,
      `/admin/avatar/list?_=${Date.now()}`,
      `/admin/avatar-gallery?_=${Date.now()}`,
      `/public/avatars?_=${Date.now()}`,
    ],
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    setMsg("");

    try {
      const res = await getTry(listUrls);
      const next = unwrapAvatars(res?.data);
      setAvatars(next);

      const usedPath =
        listUrls.find((u) => {
          const reqUrl = String(res?.config?.url || "");
          return reqUrl.includes(u.split("?")[0]);
        }) || "";

      if (usedPath.startsWith("/public/avatars")) {
        setMsg("LOAD: Using public avatar feed because admin avatar list is unavailable.");
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "List failed";

      if (status === 404) {
        setMsg("LOAD: Avatar backend endpoints are not enabled yet. Frontend is installed correctly.");
      } else if (status === 500) {
        setMsg("LOAD: 500 GET /admin/avatars :: backend avatar endpoint crashed. Frontend is ready once backend is fixed.");
      } else {
        setMsg(`LOAD: ${status || ""} :: ${detail}`);
      }

      setAvatars([]);
      console.error("[ADMIN AVATAR LOAD]", e);
    } finally {
      setLoading(false);
    }
  }, [listUrls]);

  async function uploadFiles(files: FileList) {
    setMsg("");
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files", f));

      await postTry(
        [
          "/admin/avatars/upload",
          "/admin/avatar/upload",
          "/admin/avatar-gallery/upload",
        ],
        fd
      );

      setMsg(`UPLOAD: OK (${files.length} file${files.length === 1 ? "" : "s"})`);
      await load();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || "Upload failed";
      setMsg(`UPLOAD: ${e?.response?.status || ""} :: ${detail}`);
      console.error("[ADMIN AVATAR UPLOAD]", e);
    }
  }

  async function publish() {
    setMsg("");
    try {
      await postTry(
        [
          "/admin/avatars/publish",
          "/admin/avatar/publish",
          "/admin/avatar-gallery/publish",
        ],
        {}
      );

      setMsg("PUBLISH: OK (students read /public/avatars)");
      await load();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || "Publish failed";
      setMsg(`PUBLISH: ${e?.response?.status || ""} :: ${detail}`);
      console.error("[ADMIN AVATAR PUBLISH]", e);
    }
  }

  function startEdit(id: string) {
    const item = avatarMap.get(id);
    setEditingId(id);
    setDraftLabel(clean(item?.label || item?.id));
    setMsg("");
  }

  function cancelEdit() {
    setEditingId("");
    setDraftLabel("");
  }

  async function saveEdit(id: string) {
    setMsg("");
    try {
      const label = draftLabel.trim();

      await tryJsonThenForm(
        [
          `/admin/avatars/${encodeURIComponent(id)}`,
          `/admin/avatars/${encodeURIComponent(id)}/label`,
          `/admin/avatar/${encodeURIComponent(id)}`,
          `/admin/avatar/${encodeURIComponent(id)}/label`,
        ],
        { label }
      );

      setMsg("SAVE: OK");
      setEditingId("");
      setDraftLabel("");
      await load();
    } catch (e: any) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Save failed";
      setMsg(
        `SAVE: ${status || ""} :: ${detail}\n` +
          `If backend label editing is not mounted yet, add PUT /admin/avatars/{id} or /admin/avatars/{id}/label.`
      );
      console.error("[ADMIN AVATAR SAVE]", e);
    }
  }

  async function deleteAvatar(id: string) {
    if (!window.confirm("Delete this avatar?")) return;

    setAvatars((prev) => prev.filter((x) => x.id !== id));

    setDeletingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    setMsg("");
    try {
      await delTry([
        `/admin/avatars/${encodeURIComponent(id)}`,
        `/admin/avatar/${encodeURIComponent(id)}`,
      ]);

      setMsg("DELETE: OK");

      if (editingId === id) cancelEdit();

      await load();
    } catch (e: any) {
      await load();

      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Delete failed";
      setMsg(
        `DELETE: ${status || ""} :: ${detail}\n` +
          `If backend delete is not mounted yet, add DELETE /admin/avatars/{id}.`
      );
      console.error("[ADMIN AVATAR DELETE]", e);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="aag-page">
      <div className="aag-card">
        <div className="aag-topbar">
          <div className="aag-actions">
            <button
              className="aag-btn primary"
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={loading}
            >
              Upload Images
            </button>

            <button className="aag-btn" type="button" onClick={load} disabled={loading}>
              Refresh
            </button>

            <button className="aag-btn primary" type="button" onClick={publish} disabled={loading}>
              Publish
            </button>
          </div>

          <div className="aag-meta">
            Loaded — <b>{avatars.length}</b>
          </div>
        </div>

        <div className="aag-title">Avatar Gallery</div>
        <div className="aag-sub">
          Admin is the single source of truth. Students read <code>/public/avatars</code>. Thumbnails use{" "}
          <b>contain</b> (no cropping).
        </div>

        {msg ? <div className="aag-msg">{msg}</div> : null}

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length) uploadFiles(files);
            e.currentTarget.value = "";
          }}
        />

        {avatars.length === 0 ? (
          <div className="aag-empty">
            <div className="aag-empty-title">No avatars yet</div>
            <div className="aag-empty-sub">Click Upload Images to store PNG/JPG/WebP files on the server.</div>
          </div>
        ) : (
          <div className="aag-grid">
            {avatars.map((a) => {
              const raw = pickSrc(a);
              const normalized = normalizeAvatarUrl(raw, a.id);
              const finalSrc = cacheBust(normalized);
              const isEditing = editingId === a.id;
              const isDeleting = deletingIds.has(a.id);

              return (
                <div key={a.id} className="aag-item">
                  <div className="aag-thumb">
                    {finalSrc ? (
                      <img
                        src={finalSrc}
                        alt={a.label || a.id}
                        onError={(ev) => {
                          (ev.currentTarget as HTMLImageElement).style.display = "none";
                          const parent = ev.currentTarget.parentElement;
                          if (parent && !parent.querySelector(".aag-thumb-fallback")) {
                            const fb = document.createElement("div");
                            fb.className = "aag-thumb-fallback";
                            fb.textContent = "IMG";
                            parent.appendChild(fb);
                          }
                        }}
                      />
                    ) : (
                      <div className="aag-thumb-fallback">IMG</div>
                    )}
                  </div>

                  <div className="aag-label-row">
                    {isEditing ? (
                      <input
                        className="aag-label-input"
                        value={draftLabel}
                        onChange={(e) => setDraftLabel(e.target.value)}
                        placeholder="Avatar label"
                      />
                    ) : (
                      <div className="aag-label" title={a.label || a.id}>
                        {a.label || a.id}
                      </div>
                    )}
                  </div>

                  <div className="aag-item-actions">
                    {!isEditing ? (
                      <>
                        <button
                          className="aag-mini"
                          type="button"
                          onClick={() => startEdit(a.id)}
                          disabled={isDeleting}
                        >
                          Edit
                        </button>
                        <button
                          className="aag-mini danger"
                          type="button"
                          onClick={() => deleteAvatar(a.id)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? "Deleting..." : "Delete"}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="aag-mini primary"
                          type="button"
                          onClick={() => saveEdit(a.id)}
                          disabled={isDeleting}
                        >
                          Save
                        </button>
                        <button className="aag-mini" type="button" onClick={cancelEdit} disabled={isDeleting}>
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="aag-footer">© {new Date().getFullYear()} Doctor Is In App. All rights reserved.</div>
      </div>
    </div>
  );
}